(function () {
  'use strict';

  if (window.UnifiedHealth) {
    return;
  }

  const VERSION = '1.1.0';

  /*
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function isDebugEnabled() {
    return (
      getConfig().debug === true
    );
  }

  function nowIso() {
    return new Date()
      .toISOString();
  }

  function clone(value) {
    try {
      return JSON.parse(
        JSON.stringify(value)
      );
    } catch (error) {
      return value;
    }
  }

  /*
   * ------------------------------------------------------------
   * MODULE REGISTRY
   * ------------------------------------------------------------
   */

  function getModules() {
    return {
      config:
        window.UnifiedTrackingConfig,

      schema:
        window.UnifiedSchema,

      normalizer:
        window.UnifiedNormalizer,

      validator:
        window.UnifiedValidator,

      rules_engine:
        window.UnifiedRulesEngine,

      bus:
        window.UnifiedBus,

      debug:
        window.UnifiedDebug,

      context:
        window.UnifiedContext,

      errors:
        window.UnifiedErrors,

      journey:
        window.UnifiedJourney,

      performance:
        window.UnifiedPerformance,

      consent:
        window.UnifiedConsent,

      provider_manager:
        window.UnifiedProviderManager,

      tracking:
        window.UnifiedTracking,

      detection:
        window.UnifiedDetection,

      phone_listener:
        window.UnifiedPhoneListener,

      email_listener:
        window.UnifiedEmailListener,

      form_listener:
        window.UnifiedFormListener,

      visibility_listener:
        window.UnifiedVisibilityListener,

      cta_listener:
        window.UnifiedCtaListener,

      newsletter_listener:
        window.UnifiedNewsletterListener,

      share_listener:
        window.UnifiedShareListener,

      whatsapp_listener:
        window.UnifiedWhatsappListener,

      social_listener:
        window.UnifiedSocialListener
    };
  }

  /*
   * ------------------------------------------------------------
   * REQUIRED / OPTIONAL
   * ------------------------------------------------------------
   */

  const REQUIRED_MODULES = [
    'config',
    'schema',
    'normalizer',
    'validator',
    'rules_engine',
    'bus',
    'context',
    'consent',
    'provider_manager',
    'tracking',
    'detection'
  ];

  const LISTENER_MODULES = [
    'phone_listener',
    'email_listener',
    'form_listener',
    'visibility_listener',
    'cta_listener',
    'newsletter_listener',
    'share_listener',
    'whatsapp_listener',
    'social_listener'
  ];

  /*
   * ------------------------------------------------------------
   * MODULE STATUS
   * ------------------------------------------------------------
   */

  function getModuleStatus() {
    const modules =
      getModules();

    const result = {};

    Object.keys(
      modules
    ).forEach(
      function (name) {
        const module =
          modules[name];

        result[name] = {
          loaded:
            Boolean(module),

          version:
            module &&
            module.version
              ? module.version
              : ''
        };
      }
    );

    return result;
  }

  /*
   * ------------------------------------------------------------
   * LISTENER STATUS
   * ------------------------------------------------------------
   */

  function getListenerStatus() {
    const modules =
      getModules();

    const result = {};

    LISTENER_MODULES
      .forEach(
        function (name) {
          const listener =
            modules[name];

          if (
            !listener
          ) {
            result[name] = {
              loaded: false,
              started: false
            };

            return;
          }

          let status = {};

          if (
            typeof listener.getStatus ===
              'function'
          ) {
            try {
              status =
                listener.getStatus() ||
                {};
            } catch (error) {
              status = {
                error:
                  'status_check_failed'
              };
            }
          }

          result[name] =
            Object.assign(
              {
                loaded:
                  true,

                started:
                  status.started ===
                  true
              },
              status
            );
        }
      );

    return result;
  }

  /*
   * ------------------------------------------------------------
   * CONSENT STATUS
   * ------------------------------------------------------------
   */

  function getConsentStatus() {
    const consent =
      window.UnifiedConsent;

    if (!consent) {
      return {
        loaded: false,
        ready: false,
        analytics_allowed: false,
        marketing_allowed: false
      };
    }

    let state = {};

    if (
      typeof consent.getState ===
        'function'
    ) {
      try {
        state =
          consent.getState() ||
          {};
      } catch (error) {
        state = {};
      }
    }

    return {
      loaded:
        true,

      ready:
        state.ready ===
        true,

      status:
        state.status ||
        '',

      analytics_allowed:
        state.analytics_allowed ===
        true,

      marketing_allowed:
        state.marketing_allowed ===
        true,

      region:
        state.region ||
        '',

      error:
        state.error ||
        null
    };
  }

  /*
   * ------------------------------------------------------------
   * PROVIDER STATUS
   * ------------------------------------------------------------
   */

  function getProviderStatus() {
    const manager =
      window.UnifiedProviderManager;

    if (
      !manager ||
      typeof manager.getStatus !==
        'function'
    ) {
      return {};
    }

    try {
      return manager.getStatus();
    } catch (error) {
      return {
        error:
          'provider_status_failed'
      };
    }
  }

  /*
   * ------------------------------------------------------------
   * TRACKING STATUS
   * ------------------------------------------------------------
   */

  function getTrackingStatus() {
    const tracking =
      window.UnifiedTracking;

    if (!tracking) {
      return {
        loaded: false,
        ready: false
      };
    }

    let ready = false;
    let dependencies = {};

    if (
      typeof tracking.isReady ===
        'function'
    ) {
      try {
        ready =
          tracking.isReady() ===
          true;
      } catch (error) {
        ready = false;
      }
    }

    if (
      typeof tracking.getDependencyStatus ===
        'function'
    ) {
      try {
        dependencies =
          tracking.getDependencyStatus() ||
          {};
      } catch (error) {
        dependencies = {};
      }
    }

    return {
      loaded:
        true,

      ready:
        ready,

      dependencies:
        dependencies
    };
  }

  /*
   * ------------------------------------------------------------
   * PAGE MARKUP STATUS
   * ------------------------------------------------------------
   */

  function getMarkupStatus() {
    let sections = 0;
    let ctas = 0;

    try {
      sections =
        document.querySelectorAll(
          '[data-ut-section]'
        ).length;
    } catch (error) {
      sections = 0;
    }

    try {
      ctas =
        document.querySelectorAll(
          '[data-ut-cta]'
        ).length;
    } catch (error) {
      ctas = 0;
    }

    return {
      tracked_sections:
        sections,

      tracked_ctas:
        ctas
    };
  }

  /*
   * ------------------------------------------------------------
   * DIAGNOSTIC HISTORY
   * ------------------------------------------------------------
   */

  function getErrorSummary() {
    if (
      !window.UnifiedErrors ||
      typeof window.UnifiedErrors.getStats !==
        'function'
    ) {
      return null;
    }

    try {
      return window.UnifiedErrors
        .getStats();
    } catch (error) {
      return null;
    }
  }

  function getPerformanceSummary() {
    if (
      !window.UnifiedPerformance ||
      typeof window.UnifiedPerformance.getStats !==
        'function'
    ) {
      return null;
    }

    try {
      return window.UnifiedPerformance
        .getStats();
    } catch (error) {
      return null;
    }
  }

  /*
   * ------------------------------------------------------------
   * HEALTH EVALUATION
   * ------------------------------------------------------------
   */

  function evaluate() {
    const moduleStatus =
      getModuleStatus();

    const listenerStatus =
      getListenerStatus();

    const consentStatus =
      getConsentStatus();

    const providerStatus =
      getProviderStatus();

    const trackingStatus =
      getTrackingStatus();

    const markupStatus =
      getMarkupStatus();

    const missingRequired =
      REQUIRED_MODULES
        .filter(
          function (name) {
            return (
              !moduleStatus[name] ||
              moduleStatus[name]
                .loaded !== true
            );
          }
        );

    const missingListeners =
      LISTENER_MODULES
        .filter(
          function (name) {
            return (
              !listenerStatus[name] ||
              listenerStatus[name]
                .loaded !== true
            );
          }
        );

    const stoppedListeners =
      LISTENER_MODULES
        .filter(
          function (name) {
            return (
              listenerStatus[name] &&
              listenerStatus[name]
                .loaded === true &&
              listenerStatus[name]
                .started !== true
            );
          }
        );

    const issues = [];

    /*
     * Core failures.
     */

    if (
      missingRequired.length
    ) {
      issues.push({
        severity:
          'error',

        code:
          'required_modules_missing',

        modules:
          missingRequired
      });
    }

    if (
      trackingStatus.loaded &&
      trackingStatus.ready !==
        true
    ) {
      issues.push({
        severity:
          'error',

        code:
          'tracking_not_ready'
      });
    }

    /*
     * Listener problems.
     */

    if (
      missingListeners.length
    ) {
      issues.push({
        severity:
          'warning',

        code:
          'listeners_missing',

        modules:
          missingListeners
      });
    }

    if (
      stoppedListeners.length
    ) {
      issues.push({
        severity:
          'warning',

        code:
          'listeners_not_started',

        modules:
          stoppedListeners
      });
    }

    /*
     * Consent.
     *
     * Not having analytics consent is NOT
     * a health failure. It may simply mean
     * the visitor rejected analytics.
     *
     * Consent API not being ready is a warning.
     */

    if (
      consentStatus.loaded &&
      consentStatus.ready !==
        true
    ) {
      issues.push({
        severity:
          'warning',

        code:
          'consent_not_ready',

        status:
          consentStatus.status ||
          ''
      });
    }

    /*
     * Markup is intentionally separate.
     *
     * Zero explicit sections / CTA markers
     * does not break the framework.
     */

    if (
      markupStatus.tracked_sections ===
      0
    ) {
      issues.push({
        severity:
          'info',

        code:
          'no_tracked_sections_on_page'
      });
    }

    if (
      markupStatus.tracked_ctas ===
      0
    ) {
      issues.push({
        severity:
          'info',

        code:
          'no_tracked_ctas_on_page'
      });
    }

    /*
     * Providers being disabled is expected
     * during staging and therefore not
     * considered an error.
     */

    const hasError =
      issues.some(
        function (issue) {
          return (
            issue.severity ===
            'error'
          );
        }
      );

    const hasWarning =
      issues.some(
        function (issue) {
          return (
            issue.severity ===
            'warning'
          );
        }
      );

    let status =
      'healthy';

    if (hasError) {
      status =
        'failed';
    } else if (hasWarning) {
      status =
        'degraded';
    }

    return {
      version:
        VERSION,

      framework_version:
        getConfig().version ||
        '',

      deployment:
        getConfig().deployment ||
        '',

      environment:
        getConfig().environment ||
        '',

      checked_at:
        nowIso(),

      status:
        status,

      core_ready:
        missingRequired.length ===
        0,

      tracking_ready:
        trackingStatus.ready ===
        true,

      modules:
        moduleStatus,

      listeners:
        listenerStatus,

      consent:
        consentStatus,

      providers:
        providerStatus,

      markup:
        markupStatus,

      errors:
        getErrorSummary(),

      performance:
        getPerformanceSummary(),

      issues:
        issues
    };
  }

  /*
   * ------------------------------------------------------------
   * PRINT
   * ------------------------------------------------------------
   */

  function print() {
    const report =
      evaluate();

    console.group(
      '[Unified Health] ' +
        report.status
          .toUpperCase()
    );

    console.log(
      'Framework:',
      report.framework_version
    );

    console.log(
      'Deployment:',
      report.deployment
    );

    console.log(
      'Environment:',
      report.environment
    );

    console.log(
      'Core ready:',
      report.core_ready
    );

    console.log(
      'Tracking ready:',
      report.tracking_ready
    );

    console.log(
      'Consent:',
      report.consent
    );

    console.log(
      'Providers:',
      report.providers
    );

    console.log(
      'Markup:',
      report.markup
    );

    if (
      report.issues.length
    ) {
      console.table(
        report.issues.map(
          function (issue) {
            return {
              severity:
                issue.severity,

              code:
                issue.code,

              detail:
                issue.modules
                  ? issue.modules
                      .join(', ')
                  : (
                      issue.status ||
                      ''
                    )
            };
          }
        )
      );
    }

    console.groupEnd();

    return clone(
      report
    );
  }

  /*
   * ------------------------------------------------------------
   * SIMPLE BOOLEAN CHECKS
   * ------------------------------------------------------------
   */

  function isHealthy() {
    return (
      evaluate().status ===
      'healthy'
    );
  }

  function isCoreReady() {
    return (
      evaluate()
        .core_ready === true
    );
  }

  /*
   * ------------------------------------------------------------
   * PUBLIC API
   * ------------------------------------------------------------
   */

  window.UnifiedHealth = {
    version:
      VERSION,

    evaluate:
      evaluate,

    print:
      print,

    isHealthy:
      isHealthy,

    isCoreReady:
      isCoreReady,

    getModuleStatus:
      getModuleStatus,

    getListenerStatus:
      getListenerStatus,

    getConsentStatus:
      getConsentStatus,

    getProviderStatus:
      getProviderStatus,

    getMarkupStatus:
      getMarkupStatus
  };

  if (
    isDebugEnabled()
  ) {
    console.info(
      '[Unified Health] Health Monitor initialized',
      {
        version:
          VERSION
      }
    );
  }
})();